/**
 * CardFace — renders ANY playing card face for any card game in the repo.
 *
 * Two kinds of face:
 *   - NUMBER cards (A–10): crisp vector pips on a tightened grid + a CLEAN
 *     corner index (rank stacked over one small suit glyph). No redundant suit
 *     symbol anywhere beside the rank; the index is the only corner mark.
 *   - COURT cards (J/Q/K): the GENERATED figure art (public/generated/cards/)
 *     with the vector rank-letter + suit-pip composited into the reserved
 *     top-left / bottom-right corners. The figure is suit-neutral and shared
 *     across all four suits; the corner overlay carries the suit.
 *
 * Colour-blind safety lives entirely in the vector layer: suits read by SHAPE
 * first (the four silhouettes), red carries a redundant outline + the corner
 * index repeats the suit glyph, so red vs black separate even in grayscale.
 * Legible at ~100–120px (the display size seniors actually see).
 *
 * The pip/index geometry comes from `pips.ts`; the court figure art from the
 * asset pipeline (NOT hand-drawn — depictive imagery always comes from the
 * pipeline). `CardBack` renders the generated back.
 */
import { memo } from 'react';

import { courtArtFor } from './art';
import { CardBack } from './CardBack';
import { isCourt, isRed, PIP_LAYOUT, RANK_LABEL, SUIT_PATH } from './pips';
import type { Card, Rank, Suit } from './model';

// ---------------------------------------------------------------------------
// Sub-pieces (vector)
// ---------------------------------------------------------------------------

/** One suit pip, optionally flipped 180° (for bottom-half pips). */
function Pip({
  suit,
  x,
  y,
  flip,
  size = 14,
}: {
  suit: Suit;
  x: number;
  y: number;
  flip?: boolean;
  size?: number;
}) {
  const half = size / 2;
  return (
    <g
      transform={`translate(${x - half} ${y - half}) scale(${size / 100})${flip ? ` rotate(180 50 50)` : ''}`}
      className={`card-pip ${isRed(suit) ? 'card-pip--red' : 'card-pip--black'}`}
    >
      <path d={SUIT_PATH[suit]} />
    </g>
  );
}

/**
 * Corner index — rank tightly stacked over ONE small suit glyph. Repeated
 * top-left and bottom-right (the bottom one rotated 180°). This is the ONLY
 * corner mark: no extra suit beside the rank, no second decoration.
 */
function CornerIndex({ rank, suit }: { rank: Rank; suit: Suit }) {
  return (
    <g className={`card-index ${isRed(suit) ? 'card-index--red' : 'card-index--black'}`}>
      <text x="0" y="13" className="card-index__rank">
        {RANK_LABEL[rank]}
      </text>
      <g transform="translate(0.5 15.5) scale(0.1)">
        <path d={SUIT_PATH[suit]} className="card-index__glyph" />
      </g>
    </g>
  );
}

// ---------------------------------------------------------------------------
// CardFace
// ---------------------------------------------------------------------------

export interface CardFaceProps {
  card: Card;
  /** Render the back instead of the face. */
  back?: boolean;
}

export const CardFace = memo(function CardFace({ card, back }: CardFaceProps): React.JSX.Element {
  if (back) return <CardBack />;

  const { rank, suit } = card;
  const court = isCourt(rank);
  const art = court ? courtArtFor(rank) : null;

  // The vector overlay (corner indices) is shared by number + court faces.
  // For court cards it sits ON TOP of the generated figure; for number cards it
  // sits over the pip grid drawn on the ivory field.
  const overlay = (
    <svg
      className="card-art card-art--overlay"
      viewBox="0 0 100 152"
      aria-hidden="true"
      preserveAspectRatio="none"
    >
      {!court && (
        <rect x="0.6" y="0.6" width="98.8" height="150.8" rx="8" className="card-face-bg" />
      )}

      {/* Corner indices — top-left upright, bottom-right rotated 180°. */}
      <g transform="translate(7.5 8)">
        <CornerIndex rank={rank} suit={suit} />
      </g>
      <g transform="translate(92.5 144) rotate(180)">
        <CornerIndex rank={rank} suit={suit} />
      </g>

      {/* Number-card body: pips on the tightened grid, mapped into the inner
          field (the index occupies the corners). */}
      {!court && <NumberBody rank={rank} suit={suit} />}
    </svg>
  );

  if (court && art) {
    return (
      <span className="card-art card-art--court">
        <picture className="card-art card-art--figure">
          <source type="image/avif" srcSet={art.avif} />
          <source type="image/webp" srcSet={art.webp} />
          <img src={art.png} alt="" aria-hidden="true" decoding="async" draggable={false} />
        </picture>
        {overlay}
      </span>
    );
  }

  return overlay;
});

/** Pips for A–10. The Ace is a single large central pip. */
function NumberBody({ rank, suit }: { rank: Rank; suit: Suit }): React.JSX.Element {
  const isAce = rank === 1;
  const pips = PIP_LAYOUT[rank];
  return (
    // Map the pip grid (x:0..100, y:0..100) into the inner band below the
    // corner indices, leaving room for the index at top/bottom.
    <g transform="translate(0 15) scale(1 1.145)">
      {isAce && <Pip suit={suit} x={50} y={50} size={36} />}
      {!isAce &&
        pips?.map(([x, y], i) => <Pip key={i} suit={suit} x={x} y={y} flip={y > 50} />)}
    </g>
  );
}
